import React, { useEffect, useRef, useMemo, useState } from 'react';
import proptypes from 'prop-types';
const { string, node, arrayOf, oneOfType, shape, func, bool } = proptypes;
import deepmerge from 'deepmerge';
import QueryContext from './QueryContext';
import { filterClient } from './dataModel/filterClient';
import { extend } from './dataModel/extend';
import { updateStorageAndClientCache } from './util';
import DataProviderClient from './DataProvider.client';

const walkChildrenTree = (children) => {
  return React.Children.toArray(children).map((child) => {
    if (child.type?.dataModel) {
      return child;
    }

    if (child.props?.originalType?.dataModel) {
      return child;
    }

    if (child.props?.children) {
      return walkChildrenTree(child.props.children);
    }
    return null;
  }).flat()
    .filter((val) => val);
};

export const getQueryAttributes = (children, asChildren = true, inherited) => {
  const childWrapper = asChildren ? walkChildrenTree(children) : children;
  const client = childWrapper.map((child) => {

    const type = (child.props?.originalType || child.type || {});
    return asChildren
      ? type.dataModel || {}
      : child;
  })
    .reduce((acc, cur) => {
      return deepmerge(acc, cur);
    }, inherited?.client ? inherited?.client : {});

  const server = childWrapper.map((child) => {
    const type = (child.props?.originalType || child.type || {});
    return asChildren
      ? type.dataModel || {}
      : child;
  })
    .reduce((acc, cur) => {
      const filtered = filterClient(cur);
      return deepmerge(acc, filtered);
    }, inherited?.server ? inherited?.server : {});

  return {
    client,
    server
  };
};

export const QueryProvider = ({
  dataSource,
  children,
  defaultVariables,
  mounted,
  skip,
  queryOptions,
  merge,
  cacheKey,
  persist,
  fusion,
  forceClientQueriesOnServer,
  inherit,
}) => {

  const dataStore = useRef({});
  const defaultVariablesRef = useRef({});
  const queryAttributeRef = useRef(null);
  const [persistReady, setPersistReady] = useState(false);

  if (defaultVariables) {
    defaultVariablesRef.current = defaultVariables;
  }

  // get all dataModels of children for client and server
  const queryAttributes = useMemo(() => {

    if (cacheKey
      && QueryProvider.cache[cacheKey]
      && Object.keys(QueryProvider.cache[cacheKey]).length) {
      return QueryProvider.cache[cacheKey];
    }
    if (queryAttributeRef.current) {
      return queryAttributeRef.current;
    }

    // merge + inherit + dynamic imports is not working properly
    // serverside should be adding the attribute from the dynamic import
    // (bathRenovation) but because the order it does not

    // cacheKey is different
    // needs to merge with the parent?
    // perhaps inherit should copy the attributes back to the parent

    if (merge && children.type?.dataModel && cacheKey) {

      Object.keys(merge).forEach((key) => {
        const queries = merge[key].queries
          .map((query) => {
            if (children.type.dataModel[query]) {
              return children.type.dataModel[query];
            }
            if (inherit && QueryProvider.cache[inherit].client?.[query]) {
              return QueryProvider.cache[inherit].client[query];
            }
            return null;
          })
          .filter((val) => val);
        // if inherit, need to go into
        // QueryProvider.cache[inherit].client(client contains all possible attributes)[query]
        // and add those to queries.  this will extend the merged (productClientOnlyProduct for example)
        if (queries.length > 1) {
          // eslint-disable-next-line no-param-reassign
          children.type.dataModel[key] = extend(
            ...queries
          );
        }
      });
    }

    const inherited = inherit && QueryProvider.cache[inherit]
      ? QueryProvider.cache[inherit]
      : { client: {}, server: {} };

    const attr = getQueryAttributes(children, true, inherited);

    // merged queries can be merged with inherited query provider data
    // in this case its necessary to clear the cache of the query
    // because it will be stale from the parent.
    if (inherit && merge && cacheKey) {
      Object.keys(merge).forEach((key) => {
        const queries = merge[key].queries || [];
        queries.push(key);
        queries.forEach((query) => {
          if (attr.client?.[query]) {
            delete attr.client[query]._cachedArgs;
            delete attr.client[query]._cachedQuery;
          }
          if (attr.server?.[query]) {
            delete attr.server[query]._cachedArgs;
            delete attr.server[query]._cachedQuery;
          }
        });
      });
    }

    queryAttributeRef.current = attr;
    if (cacheKey) {
      if (!inherit || (inherit && children.type?.dataModel)) {
        QueryProvider.cache[cacheKey] = attr;
      }

      // update the parent cache to use the same queries
      if (inherit && children.type?.dataModel) {
        QueryProvider.cache[inherit] = attr;
      }
    }

    return attr;
  }, [cacheKey]);

  const isClientResolved = ({ queryName }) => {
    const clientResolved = queryAttributes.client[queryName]?._isResolved;
    return !!clientResolved;
  };

  const setDefaultVariables = (props) => {
    defaultVariablesRef.current = {
      ...defaultVariables.current,
      ...props
    };
  };

  useEffect(() => {
    if (!persist) return;
    if (typeof window !== 'undefined') {
      updateStorageAndClientCache(DataProviderClient);
      setTimeout(() => {
        setPersistReady(true);
      }, 0);
    }
  }, []);

  const value = {
    dataSource,
    defaultVariables: defaultVariablesRef,
    queryAttributes,
    mounted,
    skip,
    isClientResolved,
    queryOptions,
    merge,
    cacheKey,
    persist: {
      hasPersist: persist,
      ready: persistReady,
    },
    setDefaultVariables,
    dataStore: fusion ? dataStore : null,
    forceClientQueriesOnServer,
  };

  return (
    <QueryContext.Provider value={value}>
      {children}
    </QueryContext.Provider>
  );
};

QueryProvider.propTypes = {
  children: oneOfType([
    node,
    arrayOf(node),
  ]).isRequired,
  cacheKey: string.isRequired,
  dataSource: string,
  defaultVariables: shape({}),
  mounted: func,
  skip: func,
  queryOptions: func,
  persist: bool,
  merge: shape({
    [string]: shape({
      queries: arrayOf(string),
      fn: func
    })
  }),
  fusion: bool,
  forceClientQueriesOnServer: arrayOf(string),
  /**
   * inherit allows for extend parent query provider.
   * pass the cacheKey of the parent to extend it.
   */
  inherit: string,
};

QueryProvider.defaultProps = {
  dataSource: '',
  defaultVariables: null,
  mounted: ({ mounted }) => mounted,
  persist: false,
  skip: ({ skip }) => skip,
  queryOptions: ({ options, queryName }) => options,
  merge: null,
  fusion: false,
  forceClientQueriesOnServer: [],
  inherit: null,
};
QueryProvider.cache = {};
